import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { 
  Target, 
  Eye, 
  Heart, 
  Lightbulb, 
  Award, 
  Shield
} from 'lucide-react';

const milestones = [
  { year: '2005', title: 'Founded', description: 'Launched with a single mobile unit' },
  { year: '2010', title: 'National Expansion', description: 'Expanded to 20 states' },
  { year: '2015', title: '100K Milestone', description: 'Completed 100,000 screenings' },
  { year: '2020', title: 'Digital Platform', description: 'Launched integrated analytics' },
  { year: '2024', title: 'Industry Leader', description: '#1 workplace screening provider' },
];

const values = [
  {
    icon: Heart,
    title: 'Patient-First',
    description: 'Every decision prioritizes individual health outcomes.',
  },
  {
    icon: Shield,
    title: 'Clinical Excellence',
    description: 'Protocols exceeding industry standards.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Continuous advancement in diagnostic technology.',
  },
  {
    icon: Eye,
    title: 'Transparency',
    description: 'Clear communication and honest reporting.',
  },
];

export const About = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-32 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <span className="overline mb-6 block">About Us</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Pioneering the future of<br />
              <span className="gradient-text">preventative care</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
              For nearly two decades, we've been at the forefront of workplace health screening, 
              helping organizations build healthier, more productive teams.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 border-t border-white/5" data-testid="mission-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-10 border-l-2 border-l-emerald-500"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4 font-['Outfit']">Our Mission</h2>
              <p className="text-zinc-400 leading-relaxed">
                To make preventative health screenings accessible, convenient, and effective 
                for every workplace—enabling early detection and improved health outcomes 
                for millions of employees.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-surface rounded-2xl p-10 border-l-2 border-l-zinc-700"
            >
              <div className="w-12 h-12 rounded-xl bg-zinc-800 border border-white/10 flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-zinc-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4 font-['Outfit']">Our Vision</h2>
              <p className="text-zinc-400 leading-relaxed">
                A world where preventable diseases are caught early, workplace wellness 
                is the norm, and every employee has access to care they need to live 
                their healthiest life.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-zinc-900/50" data-testid="about-stats">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: 19, suffix: '+', label: 'Years' },
              { value: 150, suffix: 'K+', label: 'Lives Impacted' },
              { value: 45, suffix: '', label: 'Mobile Units' },
              { value: 120, suffix: '+', label: 'Cities' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="text-4xl md:text-5xl font-bold text-white font-['JetBrains_Mono']">
                  <CountUp end={stat.value} duration={2} enableScrollSpy scrollSpyOnce />
                  <span className="text-emerald-500">{stat.suffix}</span>
                </p>
                <p className="text-zinc-500 mt-2 text-sm uppercase tracking-wide">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32" data-testid="values-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="overline mb-4 block">Our Values</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tighter">
              What drives us forward
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-surface rounded-2xl p-8"
                data-testid={`value-card-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                  <value.icon className="w-5 h-5 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{value.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-32 border-t border-white/5" data-testid="timeline-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="overline mb-4 block">Our Journey</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tighter">
              Two decades of impact
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-1/2" />
            
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center mb-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                data-testid={`milestone-${index}`}
              >
                <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'} pl-12 md:pl-0`}>
                  <div className="inline-block">
                    <span className="text-emerald-500 font-bold text-lg font-['JetBrains_Mono']">{milestone.year}</span>
                    <h3 className="text-xl font-semibold text-white mt-1 font-['Outfit']">{milestone.title}</h3>
                    <p className="text-zinc-500 mt-1 text-sm">{milestone.description}</p>
                  </div>
                </div>
                
                <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-emerald-500 rounded-full md:-translate-x-1/2 ring-4 ring-zinc-950" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 border-t border-white/5" data-testid="certifications-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {[
              { icon: Shield, label: 'HIPAA Compliant' },
              { icon: Award, label: 'Joint Commission' },
              { icon: Shield, label: 'ISO 9001' },
            ].map((cert, index) => (
              <div key={cert.label} className="flex items-center gap-3 text-zinc-500" data-testid={`cert-${index}`}>
                <cert.icon className="w-5 h-5 text-emerald-500/50" />
                <span className="text-sm">{cert.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
